import React, { useMemo, useState } from 'react';
import { getRepRatingTier } from '../utils/iconResolver';
import {
  aggregateLocationInvitees,
  parseOpportunityHistory,
  isClientBlocked,
} from '../utils/opportunityQualify';
import { sendTicketOpportunity, sendManualInvite, WEBHOOK_STAGGER_MS } from '../services/api';
import './OpportunityPanel.css';

function ticketIsInviteTarget(ticket) {
  return !ticket.fieldRepName;
}

function paymentBucket(rep) {
  const pm = rep.paymentMethod || '';
  const coi = rep.coiCompliant === true;
  if (pm === 'Direct Deposit' && coi) return 'dd-coi';
  if (pm === 'Direct Deposit' || pm === 'Direct Deposit - Need COI') return 'dd';
  if (pm === 'FN') return 'fn';
  return 'unknown';
}

function PaymentBadge({ rep }) {
  const bucket = paymentBucket(rep);
  if (bucket === 'dd-coi') return <span className="opp-pay opp-pay-dd-coi">DD-COI</span>;
  if (bucket === 'dd') return <span className="opp-pay opp-pay-dd-nocoi">DD</span>;
  if (bucket === 'fn') return <span className="opp-pay opp-pay-fn">FN</span>;
  return <span className="opp-pay opp-pay-unknown">—</span>;
}

function Stars({ rating }) {
  const { tier, color } = getRepRatingTier(rating);
  if (tier === 0) return <span className="rating-stars no-rating">No Rating</span>;
  return (
    <span className="rating-stars" aria-label={`${tier} of 5 stars`}>
      <span className="rating-stars-filled" style={{ color }}>{'★'.repeat(tier)}</span>
      <span className="rating-stars-empty">{'☆'.repeat(5 - tier)}</span>
    </span>
  );
}

function sentKey(repId, recordId) {
  return `${repId}:${recordId}`;
}

function applySent(row, sentKeys) {
  return {
    ...row,
    sites: row.sites.map(site => ({
      ...site,
      alreadyInvited: site.alreadyInvited || sentKeys.has(sentKey(row.rep.repId, site.recordId)),
    })),
  };
}

function minDist(row) {
  const dists = row.sites.map(s => s.dist).filter(d => Number.isFinite(d));
  if (!dists.length) return Infinity;
  return Math.min(...dists);
}

function sortRows(list) {
  return list.sort((a, b) => {
    if (b.qualifyCount !== a.qualifyCount) return b.qualifyCount - a.qualifyCount;
    const dist = minDist(a) - minDist(b);
    if (dist !== 0) return dist;
    return (a.rep.name || '').localeCompare(b.rep.name || '');
  });
}

function distanceLabel(sites) {
  const dists = sites.map(s => s.dist).filter(d => Number.isFinite(d));
  if (!dists.length) return 'Manual';
  const min = Math.min(...dists);
  const max = Math.max(...dists);
  if (Math.abs(min - max) < 0.05) return `${min.toFixed(1)} mi`;
  return `${min.toFixed(1)}–${max.toFixed(1)} mi`;
}

function sitePlace(site) {
  return [site.city, site.state].filter(Boolean).join(', ');
}

function sendableSites(row) {
  return row.sites.filter(s => {
    if (s.alreadyInvited) return false;
    if (s.manualOnly) return !s.block;
    return !s.fnBlocked;
  });
}

function manualBlockForTicket(rep, ticket) {
  if (rep.noInviting === true) return 'No Inviting';
  if (isClientBlocked(rep, ticket.clientName)) return 'Client Blocked';
  const fnReady = String(ticket.fnReadiness || '').toLowerCase().includes('ready');
  if (!fnReady && rep.paymentMethod === 'FN') return 'FN Not Ready';
  const isDD = /direct deposit/i.test(rep.paymentMethod || '');
  if (isDD && rep.coiCompliant !== true) return 'Requires COI';
  return '';
}

function repMatchesQuery(rep, raw) {
  if (!rep || rep.noInviting === true) return false;
  const trimmed = raw.trim();
  if (/^\d+$/.test(trimmed)) return rep.repId === parseInt(trimmed, 10);
  const q = normalizeSearch(trimmed);
  if (q.length < 2) return false;
  const hay = [rep.repId, rep.name, rep.phone, rep.email].map(normalizeSearch).join(' ');
  if (hay.includes(q)) return true;
  const qDigits = trimmed.replace(/\D/g, '');
  return qDigits.length > 0 && String(rep.phone || '').replace(/\D/g, '').includes(qDigits);
}

function coverageLabel(row) {
  if (row.qualifyCount === 0) return 'Manual';
  if (row.qualifyCount === row.siteCount) return `All ${row.siteCount}`;
  return `${row.qualifyCount}/${row.siteCount}`;
}

function coverageTitle(row) {
  if (row.qualifyCount === 0) return 'Not on the nearby list. Send creates a manual invite.';
  if (row.qualifyCount === row.siteCount) return `Qualifies for all ${row.siteCount} sites`;
  return `Qualifies for ${row.qualifyCount} of ${row.siteCount} sites`;
}

function normalizeSearch(val) {
  return String(val ?? '').toLowerCase().trim();
}

function BulkOpportunityInvites({ members, fieldReps }) {
  const [open, setOpen] = useState(false);
  const [coverage, setCoverage] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [sentKeys, setSentKeys] = useState(() => new Set());
  const [sendingRepId, setSendingRepId] = useState(null);
  const [sendProgress, setSendProgress] = useState('');
  const [errorByRepId, setErrorByRepId] = useState({});

  const unassigned = useMemo(
    () => (members || []).filter(ticketIsInviteTarget),
    [members]
  );
  const assignedCount = (members || []).length - unassigned.length;

  const aggregated = useMemo(
    () => aggregateLocationInvitees(unassigned, fieldReps),
    [unassigned, fieldReps]
  );

  const qualifiedRows = useMemo(() => {
    const list = aggregated.rows
      .map(row => applySent(row, sentKeys))
      .filter(row => coverage !== 'all' || row.qualifyCount === row.siteCount);
    return sortRows(list);
  }, [aggregated.rows, sentKeys, coverage]);

  const searchRaw = searchTerm.trim();
  const searchActive = searchRaw.length >= 2 || /^\d+$/.test(searchRaw);
  const searchPending = searchRaw.length > 0 && !searchActive;

  const searchRows = useMemo(() => {
    if (!searchActive) return [];
    const byId = new Map(aggregated.rows.map(row => [row.rep.repId, row]));
    const list = (fieldReps || [])
      .filter(rep => repMatchesQuery(rep, searchRaw))
      .map(rep => {
        const known = byId.get(rep.repId);
        const knownByTicket = new Map((known?.sites || []).map(site => [site.recordId, site]));
        const sites = unassigned.map(ticket => {
          const existing = knownByTicket.get(ticket.recordId);
          if (existing) {
            return {
              ...existing,
              alreadyInvited: existing.alreadyInvited || sentKeys.has(sentKey(rep.repId, ticket.recordId)),
              manualOnly: false,
              block: '',
            };
          }
          const block = manualBlockForTicket(rep, ticket);
          return {
            recordId: ticket.recordId,
            siteName: ticket.siteName || `Ticket #${ticket.recordId}`,
            city: ticket.city || '',
            state: ticket.state || '',
            dist: null,
            fnBlocked: block === 'FN Not Ready',
            alreadyInvited: parseOpportunityHistory(ticket.opportunityHistory).has(rep.repId)
              || sentKeys.has(sentKey(rep.repId, ticket.recordId)),
            preferred: false,
            manualOnly: true,
            block,
          };
        });
        return {
          rep,
          sites,
          qualifyCount: sites.filter(site => !site.manualOnly).length,
          siteCount: unassigned.length,
        };
      });
    return sortRows(list).slice(0, 25);
  }, [searchActive, searchRaw, fieldReps, aggregated.rows, unassigned, sentKeys]);

  const rows = searchActive ? searchRows : qualifiedRows;

  const allCount = aggregated.rows.filter(r => r.qualifyCount === r.siteCount).length;
  const anyCount = aggregated.rows.length;

  const handleSend = async (row) => {
    const targets = sendableSites(row);
    if (!targets.length || sendingRepId != null) return;
    setSendingRepId(row.rep.repId);
    setErrorByRepId(prev => {
      const next = { ...prev };
      delete next[row.rep.repId];
      return next;
    });
    const failed = [];
    for (let i = 0; i < targets.length; i++) {
      setSendProgress(`Sending ${i + 1} of ${targets.length}…`);
      if (i > 0) {
        await new Promise(resolve => setTimeout(resolve, WEBHOOK_STAGGER_MS));
      }
      try {
        const site = targets[i];
        if (site.manualOnly) await sendManualInvite(site.recordId, row.rep.repId);
        else await sendTicketOpportunity(site.recordId, row.rep.repId);
        const key = sentKey(row.rep.repId, targets[i].recordId);
        setSentKeys(prev => {
          const next = new Set(prev);
          next.add(key);
          return next;
        });
      } catch (err) {
        failed.push(`#${targets[i].recordId}: ${err.message || 'Send failed'}`);
      }
    }
    setSendingRepId(null);
    setSendProgress('');
    if (failed.length) {
      setErrorByRepId(prev => ({ ...prev, [row.rep.repId]: failed.join('; ') }));
    }
  };

  const summary = (() => {
    const noun = (n) => (n === 1 ? 'rep qualifies' : 'reps qualify');
    if (unassigned.length === 0) {
      return 'Every selected ticket already has a field rep, so there is no one to invite.';
    }
    if (unassigned.length === 1) {
      const site = unassigned[0].siteName || `ticket #${unassigned[0].recordId}`;
      return `${anyCount} ${noun(anyCount)} for ${site}.`;
    }
    if (coverage === 'all') {
      return `${allCount} ${noun(allCount)} for all ${unassigned.length} sites.`;
    }
    return `${anyCount} ${noun(anyCount)} for at least one of ${unassigned.length} sites.`;
  })();

  return (
    <div className="bulk-opp">
      <div
        className="detail-section-header detail-section-header-collapsible"
        onClick={() => setOpen(v => !v)}
      >
        <span className="section-toggle">{open ? '▾' : '▸'}</span>
        <span className="section-title">Send Opportunity</span>
        <span className="section-count">({coverage === 'all' ? allCount : anyCount})</span>
      </div>
      {open && (
        <div className="opportunity-panel">
          <p className="bulk-opp-summary">
            {summary}
            {assignedCount > 0 && unassigned.length > 0 && (
              <> {assignedCount} assigned ticket{assignedCount === 1 ? ' is' : 's are'} left out.</>
            )}
          </p>

          {aggregated.missingLocation.length > 0 && unassigned.length > 0 && (
            <div className="opp-note">
              No nearby-rep list on{' '}
              {aggregated.missingLocation.map(s => s.siteName).join(', ')}.
              Those sites cannot match a rep for every location.
            </div>
          )}

          {unassigned.length > 1 && (
            <div className="opp-toolbar-actions">
              <label className="opp-sort-label">
                Show
                <select
                  className="opp-sort"
                  aria-label="Invitee coverage"
                  value={coverage}
                  onChange={e => setCoverage(e.target.value)}
                >
                  <option value="all">Qualifies for all sites</option>
                  <option value="any">Qualifies for any site</option>
                </select>
              </label>
            </div>
          )}

          {unassigned.length > 0 && (
            <div className="opp-search-wrap">
              <input
                className="opp-search"
                type="search"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                placeholder="Search any rep by name, phone, email, ID"
              />
              {searchTerm && (
                <button type="button" className="opp-clear-search" onClick={() => setSearchTerm('')}>
                  Clear
                </button>
              )}
            </div>
          )}

          {searchPending && (
            <div className="opp-empty">Type at least 2 characters, or an exact rep ID.</div>
          )}

          {searchActive && rows.length === 0 && (
            <div className="opp-empty">No matching field reps.</div>
          )}

          {!searchActive && !searchPending && unassigned.length > 0 && rows.length === 0 && (
            <div className="opp-empty">
              {searchTerm.trim()
                ? 'No reps match that search.'
                : coverage === 'all' && anyCount > 0
                  ? 'No rep is close enough to every selected site.'
                  : 'No eligible nearby field reps for these sites.'}
              {coverage === 'all' && anyCount > 0 && !searchTerm.trim() && (
                <button
                  type="button"
                  className="bulk-opp-expand"
                  onClick={() => setCoverage('any')}
                >
                  Show {anyCount} who qualify for some sites
                </button>
              )}
            </div>
          )}

          {rows.length > 0 && (
          <div className="bulk-opp-list">
          {rows.map(row => {
            const targets = sendableSites(row);
            const anyInvited = row.sites.some(s => s.alreadyInvited);
            const done = targets.length === 0 && anyInvited;
            const blocked = targets.length === 0 && !anyInvited;
            const isSending = sendingRepId === row.rep.repId;
            const preferredSite = row.sites.find(s => s.preferred);
            const missing = unassigned.filter(t => !row.sites.some(s => s.recordId === t.recordId));
            const expanded = expandedId === row.rep.repId;
            let buttonLabel = `Send to ${targets.length} site${targets.length === 1 ? '' : 's'}`;
            if (isSending) buttonLabel = sendProgress || 'Sending…';
            else if (done) buttonLabel = '✓ Sent';
            else if (blocked) {
              const reasons = [...new Set(row.sites.map(site => (
                site.manualOnly ? site.block : (site.fnBlocked ? 'FN Not Ready' : '')
              )).filter(Boolean))];
              buttonLabel = reasons.length === 1 ? reasons[0] : 'Blocked';
            }

            return (
              <div
                key={row.rep.repId}
                className={`opp-card${blocked ? ' opp-card-blocked' : ''}`}
              >
                <div className="opp-card-row1">
                  <a
                    href={`https://siteservicesnow.quickbase.com/db/bjt99p7ie?a=dr&rid=${row.rep.repId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="opp-rep-name"
                  >
                    {row.rep.name || `Rep #${row.rep.repId}`}
                  </a>
                  {preferredSite && (
                    <span className="opp-pref-pill" title="Preferred tech for at least one selected site">
                      ★ Preferred
                    </span>
                  )}
                  <span className="opp-rep-id">#{row.rep.repId}</span>
                  <span className="opp-rep-dist">{distanceLabel(row.sites)}</span>
                  {(row.siteCount > 1 || row.qualifyCount === 0) && (
                    <button
                      type="button"
                      className="bulk-opp-coverage"
                      title={coverageTitle(row)}
                      onClick={() => setExpandedId(expanded ? null : row.rep.repId)}
                    >
                      {coverageLabel(row)}
                    </button>
                  )}
                </div>
                <div className="opp-card-row2">
                  <span className="opp-row-left">
                    <PaymentBadge rep={row.rep} />
                    {row.rep.phone ? <span className="opp-rep-phone">{row.rep.phone}</span> : null}
                  </span>
                  <span className="opp-row-right">
                    <Stars rating={row.rep.rating} />
                  </span>
                </div>
                {expanded && (
                  <ul className="bulk-opp-sites">
                    {row.sites.map(site => {
                      const place = sitePlace(site);
                      const flags = [];
                      if (site.alreadyInvited) flags.push('sent');
                      if (site.manualOnly && site.block) flags.push(site.block);
                      else if (site.fnBlocked) flags.push('FN not ready');
                      if (site.preferred) flags.push('preferred');
                      const dist = site.manualOnly
                        ? 'not nearby'
                        : (Number.isFinite(site.dist) ? `${site.dist.toFixed(1)} mi` : 'manual');
                      return (
                        <li key={site.recordId}>
                          {site.siteName}
                          {place ? ` · ${place}` : ''}
                          {` · ${dist}`}
                          {flags.length ? ` · ${flags.join(', ')}` : ''}
                        </li>
                      );
                    })}
                    {missing.map(ticket => (
                      <li key={ticket.recordId} className="missing">
                        {ticket.siteName || `Ticket #${ticket.recordId}`} · not nearby
                      </li>
                    ))}
                  </ul>
                )}
                <div className="opp-actions">
                  <button
                    type="button"
                    className={`opp-send-btn${done ? ' sent' : ''}${blocked ? ' fn-blocked' : ''}`}
                    disabled={targets.length === 0 || isSending || sendingRepId != null}
                    onClick={() => handleSend(row)}
                  >
                    {buttonLabel}
                  </button>
                </div>
                {errorByRepId[row.rep.repId] && (
                  <div className="opp-error">{errorByRepId[row.rep.repId]}</div>
                )}
              </div>
            );
          })}
          </div>
          )}
        </div>
      )}
    </div>
  );
}

export default BulkOpportunityInvites;
